import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Smart "Back" handler.
 *
 * Navigates to the ACTUAL previous in-app page when SPA history exists, and only falls
 * back to a canonical route (e.g. the parent list page) when the user arrived here
 * directly — a deep link, a new tab, or a fresh page load.
 *
 * Fixes the long-standing UX bug where a detail page's "Back" button always dumped the
 * user on a fixed list/landing page regardless of where they actually came from (e.g.
 * reaching a workspace from the Pipeline, then "Back" sending them to /workspaces or /).
 *
 * React Router v6 stores a monotonic `idx` on `window.history.state`; `idx > 0` means
 * there is at least one earlier in-app entry we can safely return to.
 */
export function useSmartBack(fallback = "/") {
  const navigate = useNavigate();
  return useCallback(() => {
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
    } else {
      navigate(fallback);
    }
  }, [navigate, fallback]);
}

export default useSmartBack;
